import apiEndpoints from "../config/apiEndpoints";
import axiosInstance from "../api/axiosInstance";   
import { logInfo, captureError, captureEvent } from "../utils/sentryUtils";

const captureApiError = (error, fnName, params = {}) => {
  captureError(error, {
    "service.function": fnName,
    "api.status": error?.response?.status,
    "api.url": error?.config?.url,
    "api.method": error?.config?.method,
    "api.responseData": error?.response?.data,
    params,
  });
};

export const getLanguageDetails = async (key) => {
  if (!key) {
    captureError(new Error("getLanguageDetails: key is missing"), {
      function: "getLanguageDetails",
    });
    console.error("Key is undefined");
    return null;
  }
  logInfo("Fetching language details", { key });
  try {
    const response = await axiosInstance.get(apiEndpoints.GET_LANGUAGE(key));
    captureEvent("getLanguageDetails.success", { key });
    return response;
  } catch (error) {
    captureApiError(error, "getLanguageDetails", { key });
    console.error("Error fetching language details", error?.message);
    return null;
  }
};

export const getWidgetSettings = async (language, key) => {
  if (!key || !language) {
    captureError(new Error("getWidgetSettings: key or language is missing"), {
      function: "getWidgetSettings",
      key,
      language,
    });
    console.error("Key and language are required");
    return null;
  }
  logInfo("Fetching widget settings", { key, language });
  try {
    const response = await axiosInstance.get(apiEndpoints.GET_WIDGET_SETTING(language, key));
    captureEvent("getWidgetSettings.success", { key, language });
    return response;
  } catch (error) {
    captureApiError(error, "getWidgetSettings", { key, language });
    console.error("Error fetching Widget setting", error?.message);
    return null;
  }
};

export const getWidgetTitleandInfo = async (setupID, language) => {
  if (!setupID || !language) {
    captureError(new Error("getWidgetTitleandInfo: setupID or language is missing"), {
      function: "getWidgetTitleandInfo",
      setupID,
      language,
    });
    console.error("setupID and language are required");
    return null;
  }
  logInfo("Fetching widget title and info", { setupID, language });
  try {
    const response = await axiosInstance.get(
      apiEndpoints.GET_WIDGET_TITLE_AND_INFO(setupID, language)
    );
    captureEvent("getWidgetTitleandInfo.success", { setupID, language });
    return response;
  } catch (error) {
    captureApiError(error, "getWidgetTitleandInfo", { setupID, language });
    console.error("Error fetching WIDGET_TITLE_AND_INFO", error?.message);
    return null;
  }
};

export const getWidgetSetUpPosition = async (ID) => {
  if (!ID) {
    captureError(new Error("getWidgetSetUpPosition: ID is missing"), {
      function: "getWidgetSetUpPosition",
    });
    console.error("ID is required");
    return null;
  }
  logInfo("Fetching widget setup position", { ID });
  try {
    const response = await axiosInstance.get(apiEndpoints.GET_WIDGET_POSITION(ID));
    captureEvent("getWidgetSetUpPosition.success", { ID });
    return response;
  } catch (error) {
    captureApiError(error, "getWidgetSetUpPosition", { ID });
    console.error("Error fetching Widget setup position", error?.message);
    return null;
  }
};

export const getCalenderDatas = async (key, noOfGuests, widgetId, reservationRequestMin) => {
  if (!key || !widgetId) {
    captureError(new Error("getCalenderDatas: key or widgetId is missing"), {
      function: "getCalenderDatas",
      key,
      widgetId,
    });
    console.error("key and widgetId are required");
    return null;
  }
  logInfo("Fetching calendar data", { key, noOfGuests, widgetId });
  try {
    const response = await axiosInstance.get(
      apiEndpoints.GET_CALENDER_DETAILS(key, noOfGuests, widgetId, reservationRequestMin = 0)
    );
    captureEvent("getCalenderDatas.success", { key, noOfGuests, widgetId });
    return response;
  } catch (error) {
    captureApiError(error, "getCalenderDatas", { key, noOfGuests, widgetId });
    console.error("Error fetching calender details:", error?.message);
    return null;
  }
};

export const getReservationType = async (data) => {
  if (!data) {
    captureError(new Error("getReservationType: data payload is missing"), {
      function: "getReservationType",
    });
    console.error("data is required");
    return null;
  }
  logInfo("Fetching reservation type", { data });
  try {
    const response = await axiosInstance.post(apiEndpoints.GET_RESERVATION_TYPE, data);
    captureEvent("getReservationType.success", { data });
    return response;
  } catch (error) {
    captureApiError(error, "getReservationType", { data });
    console.error("Error fetching reservation type:", error?.message);
    return null;
  }
};

export const getReservationGroup = async (data) => {
  if (!data) {
    captureError(new Error("getReservationGroup: data payload is missing"), {
      function: "getReservationGroup",
    });
    console.error("data is required");
    return null;
  }
  logInfo("Fetching reservation group", { data });
  try {
    const response = await axiosInstance.post(apiEndpoints.GET_RESERVATION_GROUP_TYPE, data);
    captureEvent("getReservationGroup.success", { data });
    return response;
  } catch (error) {
    captureApiError(error, "getReservationGroup", { data });
    console.error("Error fetching reservation group:", error?.message);
    return null;
  }
};

export const getFreeTables = async (data) => {
  if (!data) {
    captureError(new Error("getFreeTables: data payload is missing"), {
      function: "getFreeTables",
    });
    console.error("data is required");
    return null;
  }
  logInfo("Fetching free tables", { data });
  try {
    const response = await axiosInstance.post(apiEndpoints.GET_FREE_TABLES, data);
    captureEvent("getFreeTables.success", { data });
    return response;
  } catch (error) {
    captureApiError(error, "getFreeTables", { data });
    console.error("Error fetching Free tables data:", error?.message);
    return null;
  }
};

export const getReservationTypeBasedOnProducts = async (key, id) => {
  if (!key || !id) {
    captureError(new Error("getReservationTypeBasedOnProducts: key or id is missing"), {
      function: "getReservationTypeBasedOnProducts",
      key,
      id,
    });
    console.error("key and id are required");
    return null;
  }
  logInfo("Fetching reservation type based on products", { key, id });
  try {
    const response = await axiosInstance.get(
      apiEndpoints.GET_PRODUCTS_RESERVATION_TUPE(key, id)
    );
    captureEvent("getReservationTypeBasedOnProducts.success", { key, id });
    return response.data;
  } catch (error) {
    captureApiError(error, "getReservationTypeBasedOnProducts", { key, id });
    console.error("Error fetching Product reservation details:", error?.message);
    return null;
  }
};

export const getGuestDetails = async (key, ID, lang,mainModuleId) => {
  if (!key || !ID) {
    captureError(new Error("getGuestDetails: key or ID is missing"), {
      function: "getGuestDetails",
      key,
      ID,
      lang,
      mainModuleId,
    });
    console.error("key and ID are required");
    return null;
  }
  logInfo("Fetching guest details", { key, ID, lang, mainModuleId });
  try {
    const response = await axiosInstance.get(apiEndpoints.GET_GUEST_DETAILS(key, ID, lang,mainModuleId));
    captureEvent("getGuestDetails.success", { key, ID, lang, mainModuleId });
    return response;
  } catch (error) {
    captureApiError(error, "getGuestDetails", { key, ID, lang, mainModuleId });
    console.error("Error fetching guest details", error?.message);
    return null;
  }
};

export const getExtraFieldDetails = async (resID, lang, key,widgetId) => {
  if (!resID || !key) {
    captureError(new Error("getExtraFieldDetails: resID or key is missing"), {
      function: "getExtraFieldDetails",
      resID,
      lang,
      widgetId
    });
    console.error("resID and key are required");
    return null;
  }
  logInfo("Fetching extra field details", { resID, lang });
  try {
    const response = await axiosInstance.get(
      apiEndpoints.GET_EXTRA_INFO_FIELDS(resID, lang, key,widgetId)
    );
    if (response?.status === 200) {
      captureEvent("getExtraFieldDetails.success", { resID, lang });
      return response.data;
    }
    throw new Error("Failed to fetch extra fields details");
  } catch (error) {
    captureApiError(error, "getExtraFieldDetails", { resID, lang });
    console.error("Error fetching Extra fields details", error?.message);
    return null;
  }
};

export const getExtraProductDetails = async (ID, lang,key) => {
  if (!ID) {
    captureError(new Error("getExtraProductDetails: ID is missing"), {
      function: "getExtraProductDetails",
      ID,
      lang,
      key
    });
    console.error("ID is required");
    return null;
  }
  logInfo("Fetching extra product details", { ID, lang,key });
  try {
    const response = await axiosInstance.get(apiEndpoints.GET_EXTRA_PRODUCTS_DETAILS(ID, lang,key));
    captureEvent("getExtraProductDetails.success", { ID, lang,key });
    return response;
  } catch (error) {
    captureApiError(error, "getExtraProductDetails", { ID, lang,key});
    console.error("Error fetching extra product details", error?.message);
    return null;
  }
};

export const transactionCostForReservation = async (key, ID, lang) => {
  if (!key || !ID) {
    captureError(new Error("transactionCostForReservation: key or ID is missing"), {
      function: "transactionCostForReservation",
      key,
      ID,
      lang,
    });
    console.error("key and ID are required");
    return null;
  }
  logInfo("Fetching transaction cost", { key, ID, lang });
  try {
    const response = await axiosInstance.get(apiEndpoints.TRANSACTIONCOST_RESTYPE(key, ID, lang));
    captureEvent("transactionCostForReservation.success", { key, ID, lang });
    return response;
  } catch (error) {
    captureApiError(error, "transactionCostForReservation", { key, ID, lang });
    console.error("Error fetching transaction cost", error?.message);
    return null;
  }
};

export const createReservation = async (data) => {
  if (!data) {
    captureError(new Error("createReservation: data payload is missing"), {
      function: "createReservation",
    });
    console.error("data is required");
    return null;
  }
  logInfo("Creating reservation", { data });
  try {
    const response = await axiosInstance.post(apiEndpoints.CREATE_RESERVATION, data);
    captureEvent("createReservation.success", { reservationId: response?.data?.id });
    return response;
  } catch (error) {
    captureApiError(error, "createReservation", { data });
    console.error("Error in creating reservation", error?.message);
    return null;
  }
};

export const getReservationDetails = async (ID, secretkey, mainModuleId) => {
  if (!ID || !secretkey) {
    captureError(new Error("getReservationDetails: ID or secretkey is missing"), {
      function: "getReservationDetails",
      ID,
      mainModuleId,
    });
    console.error("ID and secretkey are required");
    return null;
  }
  logInfo("Fetching reservation details", { ID, mainModuleId }); // ⚠️ no secretkey in logs
  try {
    const response = await axiosInstance.get(
      apiEndpoints.GET_RESERVATION_DETAILS(ID, secretkey, mainModuleId)
    );
    captureEvent("getReservationDetails.success", { ID, mainModuleId });
    return response;
  } catch (error) {
    captureApiError(error, "getReservationDetails", { ID, mainModuleId }); // ⚠️ no secretkey
    console.error("Error fetching reservation details", error?.message);
    return null;
  }
};

export const cancelReservation = async (key, secretKey, payload) => {
  if (!key || !secretKey) {
    captureError(new Error("cancelReservation: key or secretKey is missing"), {
      function: "cancelReservation",
      key,
    });
    console.error("key and secretKey are required");
    return null;
  }
  logInfo("Cancelling reservation", { key, remarks: payload?.remarks }); // ⚠️ no secretKey
  try {
    const response = await axiosInstance.delete(apiEndpoints.CANCEL_RESERVATION, {
      data: { key, secretKey, remarks: payload?.remarks },
    });
    captureEvent("cancelReservation.success", { key, remarks: payload?.remarks });
    return response;
  } catch (error) {
    captureApiError(error, "cancelReservation", { key, remarks: payload?.remarks }); // ⚠️ no secretKey
    console.error("Error in cancelling the reservation", error?.message);
    return null;
  }
};

export const fetchCombinationWidget = async (
  idreservationtype, key, arrivaldate, arrivalTime, departureTime, totalPeople, lang
) => {
  if (!key || !idreservationtype) {
    captureError(new Error("fetchCombinationWidget: key or idreservationtype is missing"), {
      function: "fetchCombinationWidget",
      idreservationtype,
    });
    console.error("key and idreservationtype are required");
    return null;
  }
  logInfo("Fetching combination widget", { idreservationtype, arrivaldate, totalPeople, lang });
  try {
    const response = await axiosInstance.get(
      apiEndpoints.COMBINATION_WIDGET(
        idreservationtype, key, arrivaldate, arrivalTime, departureTime, totalPeople, lang
      )
    );
    captureEvent("fetchCombinationWidget.success", { idreservationtype, totalPeople });
    return response;
  } catch (error) {
    captureApiError(error, "fetchCombinationWidget", {
      idreservationtype, arrivaldate, arrivalTime, departureTime, totalPeople, lang,
    });
    console.error("Error fetching combination widget", error?.message);
    return null;
  }
};

export const GetOpeningHours = async (key, lang, url) => {
  if (!key) {
    captureError(new Error("GetOpeningHours: key is missing"), {
      function: "GetOpeningHours",
      lang,
      url,
    });
    console.error("Key is required to fetch opening hours.");
    return null;
  }
  logInfo("Fetching opening hours", { lang, url });
  try {
    const response = await axiosInstance.get(apiEndpoints.GET_OPENING_HOURS(key, lang, url));
    if (response?.status === 200) {
      captureEvent("GetOpeningHours.success", { lang, url });
      return response.data;
    }
  } catch (error) {
    captureApiError(error, "GetOpeningHours", { lang, url });
    console.error("Error fetching opening hours:", error?.message);
    return null;
  }
};

export const FetchWidgetConfigDetails = async (key, id, lang, guestid) => {
  if (!key || !id) {
    captureError(new Error("FetchWidgetConfigDetails: key or id is missing"), {
      function: "FetchWidgetConfigDetails",
      id,
      lang,
      guestid,
    });
    console.error("key and id are required");
    return null;
  }
  logInfo("Fetching widget config details", { id, lang, guestid });
  try {
    const response = await axiosInstance.get(
      apiEndpoints.GET_WIDGETS_CONFIG(key, id, lang, guestid)
    );
    if (response?.status === 200) {
      captureEvent("FetchWidgetConfigDetails.success", { id, lang, guestid });
      return response;
    }
  } catch (error) {
    captureApiError(error, "FetchWidgetConfigDetails", { id, lang, guestid });
    console.error("Error fetching widget config details:", error?.message);
    return null;
  }
};

export const getReservationPdf = async (key, secretkey, mainModuleId) => {
  if (!key || !secretkey) {
    captureError(new Error("getReservationPdf: key or secretkey is missing"), {
      function: "getReservationPdf",
      mainModuleId,
    });
    console.error("key and secretkey are required");
    return null;
  }
  logInfo("Fetching reservation PDF", { mainModuleId }); // ⚠️ no secretkey

  const normalizeResponse = (data) => {
    const pdfData = data?.pdf ?? null;
    const reservationNumber = data?.reservationNumber ?? null;
    if (!pdfData && !reservationNumber) return null;
    if (pdfData?.startsWith("data:application/pdf;base64,")) {
      return { pdf: pdfData.split(",")[1], reservationNumber };
    }
    return { pdf: pdfData, reservationNumber };
  };

  try {
    const response = await axiosInstance.get(
      apiEndpoints.GET_RESERVATION_PDF(key, encodeURIComponent(secretkey), mainModuleId),
      { headers: { "Content-Type": "application/json" } }
    );
    captureEvent("getReservationPdf.success", { mainModuleId });
    return normalizeResponse(response?.data);
  } catch (error) {
    captureApiError(error, "getReservationPdf", { mainModuleId }); // ⚠️ no secretkey
    return normalizeResponse(error?.response?.data);
  }
};

export const GetGuestDetailsByKey = async (token, key, guestId) => {
  if (!token) {
    captureError(new Error("GetGuestDetailsByKey: token is missing"), {
      function: "GetGuestDetailsByKey",
      guestId,
    });
    console.error("Token is required to fetch guest details.");
    return null;
  }
  logInfo("Fetching guest details by key", { guestId }); // ⚠️ no token
  try {
    const response = await axiosInstance.get(apiEndpoints.GET_GUEST_DETAILS_BYKEY(key, guestId), {
      headers: { "api-key": token },
    });
    if (response?.status === 200) {
      captureEvent("GetGuestDetailsByKey.success", { guestId });
      return response.data;
    }
  } catch (error) {
    captureApiError(error, "GetGuestDetailsByKey", { guestId }); // ⚠️ no token
    console.error("Error fetching guest details:", error?.message);
    return null;
  }
};




export const AuthLogin = async (data) => {
  if (!data) {
    captureError(new Error("AuthLogin: data payload is missing"), {
      function: "AuthLogin",
    });
    console.error("data is required");
    return null;
  } 
  logInfo("Authenticating user", { data });
  try {
    const response = await axiosInstance.post(apiEndpoints.AUTH_LOGIN, data);
    captureEvent("AuthLogin.success", { userId: response?.data?.userId });
    return response;
  } catch (error) {
    captureApiError(error, "AuthLogin", { data });
    console.error("Error in user authentication", error?.message);
    return null;
  } 
}  
